import React, {
  Suspense,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react'
import { graphql } from 'relay-runtime'
import { useLazyLoadQuery, useMutation } from 'react-relay'
import {
  Alert,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Radio,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
} from '@mui/material'
import { ChevronDown, ChevronRight, Trash2 } from 'lucide-react'
import { toast } from 'react-toastify'
import { AdminRole } from '../roles'
import { FeatureFlagSelector } from '../FeatureFlagSelector'
import { RoleSelect } from '../components/RoleSelect'
import {
  BrandAdvancedFields,
  BrandAdvancedValues,
  emptyBrandAdvanced,
  toBrandConfigInput,
} from './BrandAdvancedFields'
import { cancelButtonSx } from '../styles'
import { isValidEmail, metaAdAccountIdError } from '../validation'
import { AccountSetupWizardMutation } from './__generated__/AccountSetupWizardMutation.graphql'
import { AccountSetupWizardDefaultFlagsQuery } from './__generated__/AccountSetupWizardDefaultFlagsQuery.graphql'

// Flags pre-selected at the account level when creating a new account.
const DEFAULT_ACCOUNT_FLAG_NAMES = [
  'CIH',
  'PURCHASES',
  'TAGS',
  'LEADS',
  'HOOK_TEXT',
  'HEADLINE',
  'CONCEPTING',
]

const defaultFlagsQuery = graphql`
  query AccountSetupWizardDefaultFlagsQuery {
    adminListFeatureFlags {
      id
      name
    }
  }
`

/**
 * Resolves the default account-level flag ids from the catalog once and hands
 * them to the wizard. Renders nothing; kept mounted for the wizard's lifetime
 * so it doesn't re-apply when steps change.
 */
const DefaultAccountFlags = ({
  onResolve,
}: {
  onResolve: (ids: string[]) => void
}) => {
  const data = useLazyLoadQuery<AccountSetupWizardDefaultFlagsQuery>(
    defaultFlagsQuery,
    {},
    { fetchPolicy: 'store-and-network' }
  )
  const resolved = useRef(false)
  useEffect(() => {
    if (resolved.current) return
    resolved.current = true
    onResolve(
      data.adminListFeatureFlags
        .filter((flag) => DEFAULT_ACCOUNT_FLAG_NAMES.includes(flag.name))
        .map((flag) => flag.id)
    )
  }, [data, onResolve])
  return null
}

interface WizardBrand {
  name: string
  metaAdAccountId: string
  featureFlagIds: string[]
  advanced: BrandAdvancedValues
}

interface WizardUser {
  email: string
  firstName: string
  lastName: string
  accountRoles: AdminRole[]
  // brand access keyed by brand index -> roles
  brandAccess: Record<number, AdminRole[]>
  featureFlagIds: string[]
}

const emptyBrand = (): WizardBrand => ({
  name: '',
  metaAdAccountId: '',
  featureFlagIds: [],
  advanced: emptyBrandAdvanced(),
})

const emptyUser = (): WizardUser => ({
  email: '',
  firstName: '',
  lastName: '',
  accountRoles: [],
  brandAccess: {},
  featureFlagIds: [],
})

const flagFallback = (
  <div className="py-2">
    <CircularProgress size={18} />
  </div>
)

const setupMutation = graphql`
  mutation AccountSetupWizardMutation($input: SetupAccountRequest!) {
    setupAccount(input: $input) {
      id
    }
  }
`

interface AccountSetupWizardProps {
  onClose: () => void
  onCreated: () => void
}

const STEPS = ['Account', 'Brands', 'Users']

export const AccountSetupWizard = ({
  onClose,
  onCreated,
}: AccountSetupWizardProps) => {
  const [activeStep, setActiveStep] = useState(0)
  const [name, setName] = useState('')
  const [metaBusinessAccountId, setMetaBusinessAccountId] = useState('')
  const [accountFlagIds, setAccountFlagIds] = useState<string[]>([])
  const [brands, setBrands] = useState<WizardBrand[]>([emptyBrand()])
  const [users, setUsers] = useState<WizardUser[]>([])
  const [ownerIndex, setOwnerIndex] = useState<number | null>(null)

  const [setupAccount, submitting] =
    useMutation<AccountSetupWizardMutation>(setupMutation)

  // Apply the default account-level flags once, only if the admin hasn't
  // already changed the selection.
  const defaultsApplied = useRef(false)
  const handleDefaultFlags = useCallback((ids: string[]) => {
    if (defaultsApplied.current) return
    defaultsApplied.current = true
    setAccountFlagIds((prev) => (prev.length === 0 ? ids : prev))
  }, [])

  // Brands that have a name, used consistently for the user step (brand access
  // indexes into this list) and for submission.
  const namedBrands = brands.filter((b) => b.name.trim() !== '')

  const canLeaveAccount = name.trim() !== '' && metaBusinessAccountId.trim() !== ''
  const canLeaveBrands =
    namedBrands.length >= 1 &&
    namedBrands.every((b) => metaAdAccountIdError(b.metaAdAccountId) === null)
  // Every user with an email entered must have a valid one (empty users are
  // dropped on submit).
  const usersValid = users.every(
    (u) => u.email.trim() === '' || isValidEmail(u.email)
  )

  // Tracks which steps the admin has tried to leave, so errors only show after
  // an attempt.
  const [attempted, setAttempted] = useState<Record<number, boolean>>({})
  const markAttempted = (step: number) =>
    setAttempted((prev) => ({ ...prev, [step]: true }))

  const handleNext = () => {
    markAttempted(activeStep)
    if (activeStep === 0 && !canLeaveAccount) return
    if (activeStep === 1 && !canLeaveBrands) return
    setActiveStep((s) => s + 1)
  }

  // ----- brand helpers ------------------------------------------------------
  const updateBrandName = (index: number, value: string) =>
    setBrands((prev) =>
      prev.map((b, i) => (i === index ? { ...b, name: value } : b))
    )
  const updateBrandMetaAdAccount = (index: number, value: string) =>
    setBrands((prev) =>
      prev.map((b, i) => (i === index ? { ...b, metaAdAccountId: value } : b))
    )
  const updateBrandFlags = (index: number, featureFlagIds: string[]) =>
    setBrands((prev) =>
      prev.map((b, i) => (i === index ? { ...b, featureFlagIds } : b))
    )
  const updateBrandAdvanced = (
    index: number,
    patch: Partial<BrandAdvancedValues>
  ) =>
    setBrands((prev) =>
      prev.map((b, i) =>
        i === index ? { ...b, advanced: { ...b.advanced, ...patch } } : b
      )
    )
  const [brandAdvancedOpen, setBrandAdvancedOpen] = useState<
    Record<number, boolean>
  >({})
  const toggleBrandAdvanced = (index: number) =>
    setBrandAdvancedOpen((prev) => ({ ...prev, [index]: !prev[index] }))
  const addBrand = () => setBrands((prev) => [...prev, emptyBrand()])
  const removeBrand = (index: number) =>
    setBrands((prev) => prev.filter((_, i) => i !== index))

  // ----- user helpers -------------------------------------------------------
  const updateUser = (index: number, patch: Partial<WizardUser>) =>
    setUsers((prev) =>
      prev.map((u, i) => (i === index ? { ...u, ...patch } : u))
    )
  const addUser = () => setUsers((prev) => [...prev, emptyUser()])
  const removeUser = (index: number) => {
    setUsers((prev) => prev.filter((_, i) => i !== index))
    setOwnerIndex((current) =>
      current === index ? null : current != null && current > index ? current - 1 : current
    )
  }

  // Flags already covered for a user by account level + the brands they can
  // access — hidden from the user's own flag selector.
  const excludedFlagsForUser = (user: WizardUser): string[] => {
    const ids = new Set<string>(accountFlagIds)
    Object.keys(user.brandAccess).forEach((key) => {
      const brand = namedBrands[Number(key)]
      brand?.featureFlagIds.forEach((id) => ids.add(id))
    })
    return Array.from(ids)
  }

  const handleSubmit = () => {
    markAttempted(2)
    if (!canLeaveBrands || !usersValid) return
    const input = {
      name: name.trim(),
      metaBusinessAccountId: metaBusinessAccountId.trim(),
      featureFlagIds: accountFlagIds,
      brands: namedBrands.map((brand) => ({
        name: brand.name.trim(),
        metaAdAccountExternalId: brand.metaAdAccountId.trim(),
        featureFlagIds: brand.featureFlagIds,
        ...toBrandConfigInput(brand.advanced),
      })),
      users: users
        .filter((u) => u.email.trim() !== '')
        .map((u, index) => ({
          email: u.email.trim(),
          firstName: u.firstName || undefined,
          lastName: u.lastName || undefined,
          isOwner: ownerIndex === index,
          accountRoles: u.accountRoles,
          brandAccess: Object.entries(u.brandAccess)
            // Only keep access to brands that still exist by index.
            .filter(([brandIndex]) => Number(brandIndex) < namedBrands.length)
            .map(([brandIndex, roles]) => ({
              brandIndex: Number(brandIndex),
              roles,
            })),
          featureFlagIds: u.featureFlagIds,
        })),
    }

    setupAccount({
      variables: { input },
      onCompleted: () => {
        toast.success('Account created.')
        onCreated()
        onClose()
      },
      onError: (error) => {
        console.error(error)
        toast.error(error.message || 'Unable to create account.')
      },
    })
  }

  const renderAccountStep = () => (
    <>
      <TextField
        autoFocus
        fullWidth
        margin="normal"
        label="Account Name"
        value={name}
        onChange={(event) => setName(event.target.value)}
        required
        error={attempted[0] && name.trim() === ''}
        helperText={
          attempted[0] && name.trim() === ''
            ? 'Account Name is required'
            : undefined
        }
      />
      <TextField
        fullWidth
        margin="normal"
        label="Meta Business Account ID"
        value={metaBusinessAccountId}
        onChange={(event) => setMetaBusinessAccountId(event.target.value)}
        required
        error={attempted[0] && metaBusinessAccountId.trim() === ''}
        helperText={
          attempted[0] && metaBusinessAccountId.trim() === ''
            ? 'Meta Business Account ID is required'
            : undefined
        }
      />
      <Divider sx={{ my: 2 }} />
      <Suspense fallback={flagFallback}>
        <FeatureFlagSelector
          value={accountFlagIds}
          onChange={setAccountFlagIds}
          scope="account"
        />
      </Suspense>
    </>
  )

  const renderBrandsStep = () => (
    <div>
      <Typography
        variant="body2"
        color={attempted[1] && !canLeaveBrands ? 'error' : 'text.secondary'}
        sx={{ mb: 1 }}
      >
        Add at least one brand for this account.
      </Typography>
      {brands.map((brand, index) => (
        <div key={index} className="mb-3 rounded border border-gray-200 p-3">
          <div className="flex items-center gap-2">
            <TextField
              fullWidth
              margin="normal"
              label={`Brand ${index + 1}`}
              value={brand.name}
              onChange={(event) => updateBrandName(index, event.target.value)}
            />
            <IconButton
              onClick={() => removeBrand(index)}
              disabled={brands.length === 1}
            >
              <Trash2 size={16} />
            </IconButton>
          </div>
          {brand.name.trim() !== '' && (
            <>
              <TextField
                fullWidth
                margin="normal"
                label="Meta Ad Account ID"
                value={brand.metaAdAccountId}
                onChange={(event) =>
                  updateBrandMetaAdAccount(index, event.target.value)
                }
                required
                error={
                  attempted[1] &&
                  metaAdAccountIdError(brand.metaAdAccountId) !== null
                }
                helperText={
                  attempted[1] &&
                  metaAdAccountIdError(brand.metaAdAccountId) !== null
                    ? metaAdAccountIdError(brand.metaAdAccountId)
                    : 'Format: act_<id>. For agency accounts, use the brand’s own ad account ID.'
                }
              />
              <Suspense fallback={flagFallback}>
                <FeatureFlagSelector
                  value={brand.featureFlagIds}
                  excludeIds={accountFlagIds}
                  onChange={(ids) => updateBrandFlags(index, ids)}
                  scope="brand"
                />
              </Suspense>

              <div
                className="mt-3 flex cursor-pointer items-center justify-between"
                onClick={() => toggleBrandAdvanced(index)}
              >
                <Typography variant="subtitle2" color="primary">
                  Advanced
                </Typography>
                <IconButton size="small" aria-label="toggle advanced">
                  {brandAdvancedOpen[index] ? (
                    <ChevronDown size={16} />
                  ) : (
                    <ChevronRight size={16} />
                  )}
                </IconButton>
              </div>
              <Collapse in={!!brandAdvancedOpen[index]}>
                <BrandAdvancedFields
                  values={brand.advanced}
                  onChange={(patch) => updateBrandAdvanced(index, patch)}
                />
              </Collapse>
            </>
          )}
        </div>
      ))}
      <Button onClick={addBrand} sx={{ mt: 1 }}>
        Add another brand
      </Button>
    </div>
  )

  const renderUsersStep = () => (
    <div>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        Optionally add users. Mark one as the account owner.
      </Typography>
      <Alert severity="info" sx={{ mb: 2 }}>
        Super users must be created from the Users section after account setup.
      </Alert>
      {users.map((user, index) => (
        <div
          key={index}
          className="mb-3 rounded border border-gray-200 p-3"
        >
          <div className="flex items-center justify-between">
            <Typography variant="subtitle2" color="primary">
              User {index + 1}
            </Typography>
            <div className="flex items-center gap-2">
              <FormControlLabel
                control={
                  <Radio
                    size="small"
                    checked={ownerIndex === index}
                    onChange={() => setOwnerIndex(index)}
                  />
                }
                label="Owner"
              />
              <IconButton onClick={() => removeUser(index)}>
                <Trash2 size={16} />
              </IconButton>
            </div>
          </div>

          <div className="flex gap-2">
            <TextField
              fullWidth
              margin="normal"
              label="First Name"
              value={user.firstName}
              onChange={(event) =>
                updateUser(index, { firstName: event.target.value })
              }
            />
            <TextField
              fullWidth
              margin="normal"
              label="Last Name"
              value={user.lastName}
              onChange={(event) =>
                updateUser(index, { lastName: event.target.value })
              }
            />
          </div>
          <TextField
            fullWidth
            margin="normal"
            label="Email"
            type="email"
            value={user.email}
            onChange={(event) => updateUser(index, { email: event.target.value })}
            required
            error={user.email.trim() !== '' && !isValidEmail(user.email)}
            helperText={
              user.email.trim() !== '' && !isValidEmail(user.email)
                ? 'Enter a valid email address'
                : undefined
            }
          />
          <RoleSelect
            label="Account Roles"
            fullWidth
            margin="normal"
            value={user.accountRoles}
            onChange={(roles) => updateUser(index, { accountRoles: roles })}
          />

          {namedBrands.length > 0 && (
            <div className="py-1">
              <Typography variant="subtitle2" color="primary">
                Brand Access
              </Typography>
              {namedBrands.map((brand, brandIndex) => {
                const roles = user.brandAccess[brandIndex]
                const checked = roles != null
                return (
                  <div
                    key={brandIndex}
                    className="flex items-center justify-between gap-2"
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={checked}
                          onChange={(event) => {
                            const next = { ...user.brandAccess }
                            if (event.target.checked) {
                              next[brandIndex] = ['EDITOR']
                            } else {
                              delete next[brandIndex]
                            }
                            updateUser(index, { brandAccess: next })
                          }}
                        />
                      }
                      label={brand.name}
                    />
                    {checked && (
                      <RoleSelect
                        label="Roles"
                        fullWidth
                        margin="normal"
                        value={roles}
                        onChange={(newRoles) =>
                          updateUser(index, {
                            brandAccess: {
                              ...user.brandAccess,
                              [brandIndex]: newRoles,
                            },
                          })
                        }
                      />
                    )}
                  </div>
                )
              })}
            </div>
          )}

          <Divider sx={{ my: 1.5 }} />
          <Suspense fallback={flagFallback}>
            <FeatureFlagSelector
              value={user.featureFlagIds}
              excludeIds={excludedFlagsForUser(user)}
              onChange={(ids) => updateUser(index, { featureFlagIds: ids })}
              scope="user"
            />
          </Suspense>
        </div>
      ))}
      <Button onClick={addUser}>Add user</Button>
    </div>
  )

  return (
    <Dialog
      open
      onClose={onClose}
      fullWidth
      PaperProps={{ sx: { width: '100%', maxWidth: 680 } }}
    >
      <DialogTitle>Add Account</DialogTitle>
      <DialogContent>
        <Suspense fallback={null}>
          <DefaultAccountFlags onResolve={handleDefaultFlags} />
        </Suspense>
        <Stepper activeStep={activeStep} sx={{ mb: 3, mt: 1 }}>
          {STEPS.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>
        {activeStep === 0 && renderAccountStep()}
        {activeStep === 1 && renderBrandsStep()}
        {activeStep === 2 && renderUsersStep()}
      </DialogContent>
      <DialogActions>
        {activeStep > 0 && (
          <Button
            variant="outlined"
            onClick={() => setActiveStep((s) => s - 1)}
            disabled={submitting}
          >
            Back
          </Button>
        )}
        {activeStep < STEPS.length - 1 ? (
          <Button variant="contained" onClick={handleNext}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={submitting}
            startIcon={
              submitting ? (
                <CircularProgress size={16} color="inherit" />
              ) : undefined
            }
          >
            Create
          </Button>
        )}
        <Button
          variant="contained"
          sx={cancelButtonSx}
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default AccountSetupWizard
